import React, { useEffect, useMemo, useState } from 'react';
import { authApi } from '../auth/api.js';
import {
  AUTH_METHODS,
  PACKAGE_EXPECTATIONS,
  PORTAL_TYPES,
  downloadTextFile,
  draftToDealStub,
  emptyDraftForm,
  exportOnboardBriefMarkdown,
  parseAccessGrantEmail,
  proposeDealIdSlug,
  proposeMark,
  upsertLocalDraft,
} from '../deals/dealDrafts.js';

const STEPS = ['basics', 'vdr', 'confirm'];

/**
 * Admin-only multi-step New project wizard — captures VDR download / portal details.
 */
export default function NewProjectWizard({ open, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyDraftForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedVia, setSavedVia] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm(emptyDraftForm());
    setError(null);
    setSavedVia(null);
  }, [open]);

  const stubPreview = useMemo(() => draftToDealStub(form), [form]);

  if (!open) return null;

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));
  const patchPortal = (partial) =>
    setForm((f) => ({ ...f, portal: { ...f.portal, ...partial } }));

  const applyPaste = () => {
    const parsed = parseAccessGrantEmail(form.pasteGrantEmail);
    patchPortal(parsed);
  };

  const onNameOrBasin = (partial) => {
    setForm((f) => {
      const next = { ...f, ...partial };
      const autoId = !f.dealId || f.dealId === proposeDealIdSlug(f);
      const autoMark = !f.mark || f.mark === proposeMark(f.label);
      if (autoId) next.dealId = proposeDealIdSlug(next);
      if (autoMark && next.label) next.mark = proposeMark(next.label);
      if (!f.shortLabel && next.label) next.shortLabel = next.label.split(/\s+/)[0];
      return next;
    });
  };

  const togglePkg = (id) => {
    setForm((f) => {
      const set = new Set(f.packagesExpected || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, packagesExpected: [...set] };
    });
  };

  const canNextBasics = Boolean(form.label.trim() && form.dealId.trim());
  const canNextVdr = Boolean(form.portal?.url?.trim() || form.portal?.roomName?.trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    const stub = draftToDealStub(form);
    const body = {
      dealId: stub.dealId,
      label: stub.label,
      shortLabel: stub.shortLabel,
      mark: stub.mark,
      subtitle: stub.subtitle,
      packageName: stub.packageName,
      seller: stub.seller,
      advisor: stub.advisor,
      commodity: stub.commodity,
      primaryProduct: stub.primaryProduct,
      region: stub.region,
      bidsDueLabel: stub.bidsDueLabel,
      asOfLabel: form.asOfLabel || null,
      portal: stub.vdrOnboard.portal,
      packagesExpected: stub.vdrOnboard.packagesExpected,
    };
    let via = 'localStorage';
    try {
      const remote = await authApi.createDealDraft(body);
      via = 'api';
      upsertLocalDraft(remote);
      onSaved?.(remote);
    } catch (err) {
      // Fallback: local draft + downloadable onboard brief (documented in PR).
      upsertLocalDraft(stub);
      downloadTextFile(
        `${stub.dealId}-onboard-brief.md`,
        exportOnboardBriefMarkdown(stub),
      );
      downloadTextFile(`${stub.dealId}-deal-draft.json`, JSON.stringify(stub, null, 2), 'application/json');
      via = 'localStorage+export';
      onSaved?.(stub);
      if (err?.status && err.status !== 404 && err.status !== 503) {
        setError(
          `Server save failed (${err.message}). Draft kept locally and onboard brief downloaded.`,
        );
      }
    }
    setSavedVia(via);
    setSaving(false);
    if (via === 'api' || via === 'localStorage+export') {
      onClose?.();
    }
  };

  return (
    <div className="wizard-overlay" role="presentation" onClick={onClose}>
      <div
        className="wizard-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-wizard-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wizard-header">
          <h2 id="new-project-wizard-title">Create new project</h2>
          <p className="wizard-lead">
            Capture VDR portal details for onboard. Passwords are never stored — use auth method +
            optional secret ref only.
          </p>
          <ol className="wizard-steps" aria-label="Wizard steps">
            {STEPS.map((s, i) => (
              <li key={s} className={i === step ? 'active' : i < step ? 'done' : ''}>
                {i + 1}. {s === 'basics' ? 'Deal basics' : s === 'vdr' ? 'VDR portal' : 'Confirm'}
              </li>
            ))}
          </ol>
        </header>

        <div className="wizard-body">
          {step === 0 && (
            <div className="wizard-grid">
              <label>
                Project name
                <input
                  value={form.label}
                  onChange={(e) => onNameOrBasin({ label: e.target.value })}
                  placeholder="e.g. Eagle East TX"
                  autoFocus
                />
              </label>
              <label>
                Basin / region
                <input
                  value={form.basin}
                  onChange={(e) => onNameOrBasin({ basin: e.target.value, region: e.target.value })}
                  placeholder="e.g. Permian"
                />
              </label>
              <label>
                Commodity
                <select
                  value={form.commodity}
                  onChange={(e) =>
                    patch({
                      commodity: e.target.value,
                      primaryProduct: e.target.value.split('+')[0],
                    })
                  }
                >
                  <option value="gas">Gas</option>
                  <option value="oil">Oil</option>
                  <option value="oil+gas">Oil + gas</option>
                  <option value="oil+gas+ngl">Oil + gas + NGL</option>
                </select>
              </label>
              <label>
                Proposed dealId
                <input
                  value={form.dealId}
                  onChange={(e) => patch({ dealId: e.target.value.toLowerCase() })}
                  placeholder="name-basin-year"
                />
              </label>
              <label>
                Mark
                <input
                  value={form.mark}
                  maxLength={4}
                  onChange={(e) => patch({ mark: e.target.value.toUpperCase() })}
                />
              </label>
              <label>
                Seller
                <input value={form.seller} onChange={(e) => patch({ seller: e.target.value })} />
              </label>
              <label>
                Advisor
                <input value={form.advisor} onChange={(e) => patch({ advisor: e.target.value })} />
              </label>
              <label>
                Bids due (if known)
                <input
                  value={form.bidsDueLabel}
                  onChange={(e) => patch({ bidsDueLabel: e.target.value })}
                  placeholder="e.g. Oct 15, 2026 · noon CT"
                />
              </label>
            </div>
          )}

          {step === 1 && (
            <>
              <label className="wizard-paste">
                Optional — paste access-grant email
                <textarea
                  rows={4}
                  value={form.pasteGrantEmail}
                  onChange={(e) => patch({ pasteGrantEmail: e.target.value })}
                  placeholder="Paste the VDR invite / access email. Assistant-style parse fills portal fields (never stores passwords)."
                />
              </label>
              <button type="button" className="wizard-secondary" onClick={applyPaste}>
                Parse grant email into fields
              </button>
              <div className="wizard-grid">
                <label>
                  Portal type
                  <select
                    value={form.portal.type}
                    onChange={(e) => patchPortal({ type: e.target.value })}
                  >
                    {PORTAL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Portal URL
                  <input
                    value={form.portal.url}
                    onChange={(e) => patchPortal({ url: e.target.value })}
                    placeholder="https://…"
                    data-testid="wizard-portal-url"
                  />
                </label>
                <label>
                  Room name
                  <input
                    value={form.portal.roomName}
                    onChange={(e) => patchPortal({ roomName: e.target.value })}
                  />
                </label>
                <label>
                  Entitled email
                  <input
                    value={form.portal.entitledEmail}
                    onChange={(e) => patchPortal({ entitledEmail: e.target.value })}
                  />
                </label>
                <label>
                  Auth method
                  <select
                    value={form.portal.authMethod}
                    onChange={(e) => patchPortal({ authMethod: e.target.value })}
                  >
                    {AUTH_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Secret ref (optional placeholder)
                  <input
                    value={form.portal.secretRef}
                    onChange={(e) => patchPortal({ secretRef: e.target.value })}
                    placeholder="e.g. 1password://vault/item — never a raw password"
                  />
                </label>
                <label className="wizard-span-2">
                  Access notes
                  <textarea
                    rows={2}
                    value={form.portal.accessNotes}
                    onChange={(e) => patchPortal({ accessNotes: e.target.value })}
                  />
                </label>
                <label>
                  As-of (if known)
                  <input
                    value={form.asOfLabel}
                    onChange={(e) => patch({ asOfLabel: e.target.value })}
                  />
                </label>
              </div>
              <fieldset className="wizard-packages">
                <legend>Key packages expected</legend>
                {PACKAGE_EXPECTATIONS.map((p) => (
                  <label key={p.id} className="wizard-check">
                    <input
                      type="checkbox"
                      checked={(form.packagesExpected || []).includes(p.id)}
                      onChange={() => togglePkg(p.id)}
                    />
                    {p.label}
                  </label>
                ))}
              </fieldset>
            </>
          )}

          {step === 2 && (
            <div className="wizard-summary">
              <h3>Confirm draft stub</h3>
              <dl>
                <dt>dealId</dt>
                <dd>
                  <code>{stubPreview.dealId}</code>
                </dd>
                <dt>Label</dt>
                <dd>
                  {stubPreview.label} ({stubPreview.mark})
                </dd>
                <dt>Commodity</dt>
                <dd>{stubPreview.commodity}</dd>
                <dt>Portal</dt>
                <dd>
                  {stubPreview.vdrOnboard.portal.type}
                  {stubPreview.vdrOnboard.portal.url
                    ? ` · ${stubPreview.vdrOnboard.portal.url}`
                    : ''}
                  {stubPreview.vdrOnboard.portal.roomName
                    ? ` · ${stubPreview.vdrOnboard.portal.roomName}`
                    : ''}
                </dd>
                <dt>Auth</dt>
                <dd>
                  {stubPreview.vdrOnboard.portal.authMethod}
                  {stubPreview.vdrOnboard.portal.secretRef
                    ? ` · ref ${stubPreview.vdrOnboard.portal.secretRef}`
                    : ''}
                </dd>
                <dt>Packages</dt>
                <dd>
                  {(stubPreview.vdrOnboard.packagesExpected || []).join(', ') || '(none)'}
                </dd>
              </dl>
              <p className="wizard-foot-note">
                Saves under <code>ENERGY_AUTH_DATA/deal-drafts/</code> via admin API when available;
                otherwise localStorage + downloadable <code>deal-draft.json</code> / onboard brief.
              </p>
              {error ? <p className="wizard-error">{error}</p> : null}
              {savedVia ? <p className="wizard-ok">Saved via {savedVia}.</p> : null}
            </div>
          )}
        </div>

        <footer className="wizard-footer">
          <button type="button" className="wizard-secondary" onClick={onClose}>
            Cancel
          </button>
          <div className="wizard-footer-right">
            {step > 0 ? (
              <button type="button" className="wizard-secondary" onClick={() => setStep((s) => s - 1)}>
                Back
              </button>
            ) : null}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                className="wizard-primary"
                disabled={step === 0 ? !canNextBasics : !canNextVdr}
                onClick={() => setStep((s) => s + 1)}
              >
                Next
              </button>
            ) : (
              <button type="button" className="wizard-primary" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save draft stub'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
